import React, { useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Heading from 'components/partials/heading/heading';
import Button from 'components/partials/button/button';
import Loader from 'components/partials/loader/loader';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import AidStudioStepper from './components/aid-studio-stepper';
import { BackToModelsButton, ModelHeader } from './components/model-header';
import { CopyModelModal } from './components/model-modals';
import { ArchiveModelModal } from '../components/archive-model-modal';
import {
  QuickSummaryPanel,
  useQuickSummary,
} from './components/quick-summary-panel';
import BasicInfoStep from './steps/basic-info-step';
import FundsStep from './steps/funds-step';
import TuitionStep from './steps/tuition-step';
import MatrixStep from './steps/matrix-step';
import CalculationsStep from './steps/calculations-step';
import ResultsStep from './steps/results-step';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useAidStudioForm } from './hooks/use-aid-studio-form';
import { useStepNavigation } from './hooks/use-step-navigation';
import { getCopyModalHeader, useModelCopy } from './hooks/use-model-copy';
import { useModelArchive } from '../hooks/use-model-archive';

import { handleAidModelError } from './utils/aid-model-error-handler';
import {
  getAidStudioListPath,
  getCloneLandingPath,
} from './utils/aid-studio-paths';

import { AID_STUDIO_STEP } from './aid-studio-details.constants';

interface ActiveStepRef {
  hasChanges?: boolean;
  save?: () => Promise<void> | void;
  discard?: () => void;
}

interface AidStudioDetailsProps {
  isUserView?: boolean;
  clientId?: number;
}

const AidStudioDetails = ({
  isUserView,
  clientId: clientIdProp,
}: AidStudioDetailsProps): React.JSX.Element | null => {
  const { clientId: clientIdParam, modelId, step: stepParam } = useParams();
  const activeStepRef = useRef<ActiveStepRef | null>(null);

  const clientId = clientIdProp ?? Number(clientIdParam);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId ?? '';

  const navigate = useNavigate();

  const aidStudioListPath = getAidStudioListPath(clientId, isUserView);

  const basePath = modelId
    ? `${aidStudioListPath}/${modelId}`
    : `${aidStudioListPath}/new`;

  const {
    control,
    errors,
    isValid,
    isDirty,
    handleSubmit,
    getValues,
    isNewModel,
    isSaving,
    isLoading,
    isError,
    errorMessage,
    modelData,
    datasetData,
    sourceTablesOptions,
    sourceTablesLoading,
    sourceTablesFetching,
    recreateDataset,
    handleRecreateDatasetChange,
    handleSave,
    handleRename,
    handleCancel,
    handleDiscard,
  } = useAidStudioForm({
    clientId: String(clientId ?? ''),
    canGetSourceTables: !isUserView,
    modelId,
    isUserView,
  });

  const {
    activeStep,
    visibleSteps,
    isLastStep,
    stepHasChanges,
    handleStepChange,
    handleStepDirtyChange,
  } = useStepNavigation({
    isNewModel,
    stepParam,
    basePath,
    activeStepRef,
    isUserView,
  });

  const isReadOnly = !isNewModel && modelData?.canEdit === false;
  const hasUnsavedChanges = isDirty || recreateDataset || stepHasChanges;

  const quickSummary = useQuickSummary({
    clientId: String(clientId ?? ''),
    isNewModel,
  });

  const copyLandingPath = useCallback(
    (newModelId: number): string =>
      getCloneLandingPath(clientId, newModelId, isUserView),
    [clientId, isUserView]
  );

  const modelCopy = useModelCopy({
    userId,
    clientId,
    sourceModelId: modelId,
    landingPath: copyLandingPath,
    hasUnsavedChanges,
    getFormValues: getValues,
  });

  const goToModelsList = useCallback((): void => {
    navigate(aidStudioListPath);
  }, [navigate, aidStudioListPath]);

  const archive = useModelArchive({
    userId,
    clientId,
    onArchived: goToModelsList,
    onRestored: goToModelsList,
  });

  const onSubmit = async (
    data: Parameters<typeof handleSave>[0]
  ): Promise<void> => {
    try {
      await handleSave(data);
    } catch (error) {
      handleAidModelError(error, 'Failed to save model');
    }
  };

  const submitOrThrow = useCallback(
    (): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        handleSubmit(
          async (data) => {
            try {
              await handleSave(data);
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          () => reject(new Error('Form validation failed'))
        )();
      }),
    [handleSubmit, handleSave]
  );

  const basicInfoSave = useCallback(() => submitOrThrow(), [submitOrThrow]);

  const handleSaveModel = useCallback(async () => {
    try {
      if (activeStep === AID_STUDIO_STEP.BASIC_INFO) {
        await submitOrThrow();
      } else {
        await activeStepRef.current?.save?.();
      }
    } catch (error) {
      handleAidModelError(error, 'Failed to save model');
    }
  }, [activeStep, submitOrThrow]);

  const handleRevertChanges = useCallback(() => {
    if (activeStep === AID_STUDIO_STEP.BASIC_INFO) {
      handleDiscard();
    } else {
      activeStepRef.current?.discard?.();
    }
  }, [activeStep, handleDiscard]);

  const handleFullResults = useCallback(() => {
    quickSummary.close();
    handleStepChange(AID_STUDIO_STEP.RESULTS);
  }, [quickSummary.close, handleStepChange]);

  const renderStepContent = (): React.JSX.Element | null => {
    switch (activeStep) {
      case AID_STUDIO_STEP.BASIC_INFO:
        return (
          <BasicInfoStep
            ref={activeStepRef as never}
            control={control as never}
            errors={errors as never}
            isDirty={isDirty}
            isValid={isValid}
            onSave={basicInfoSave}
            sourceTablesOptions={sourceTablesOptions}
            sourceTablesLoading={sourceTablesLoading}
            sourceTablesFetching={sourceTablesFetching}
            isNewModel={isNewModel}
            recreateDataset={recreateDataset}
            onRecreateDatasetChange={handleRecreateDatasetChange}
            datasetData={datasetData}
            isUserView={isUserView}
            readOnly={isReadOnly}
          />
        );
      case AID_STUDIO_STEP.FUNDS:
        return (
          <FundsStep
            key={modelId}
            ref={activeStepRef as never}
            clientId={clientId}
            readOnly={isReadOnly}
            onDirtyChange={handleStepDirtyChange}
          />
        );
      case AID_STUDIO_STEP.TUITION:
        return (
          <TuitionStep
            key={modelId}
            ref={activeStepRef as never}
            readOnly={isReadOnly}
            onDirtyChange={handleStepDirtyChange}
          />
        );
      case AID_STUDIO_STEP.MATRIX:
        return (
          <MatrixStep
            key={modelId}
            ref={activeStepRef as never}
            clientId={clientId}
            readOnly={isReadOnly}
            onDirtyChange={handleStepDirtyChange}
            isUserView={isUserView}
          />
        );
      case AID_STUDIO_STEP.CALCULATIONS:
        return (
          <CalculationsStep
            key={modelId}
            ref={activeStepRef as never}
            clientId={clientId}
            readOnly={isReadOnly}
            onDirtyChange={handleStepDirtyChange}
          />
        );
      case AID_STUDIO_STEP.RESULTS:
        return (
          <ResultsStep
            key={modelId}
            ref={activeStepRef as never}
            clientId={clientId}
            readOnly={isReadOnly}
          />
        );
      default:
        return null;
    }
  };

  if (isError && !isNewModel) {
    return (
      <ResponsiveWrapper>
        <main className="text-center py-8">
          <p className="text-critical-600">{errorMessage}</p>
          <Button variant="reserve-text" onClick={handleCancel}>
            Go back
          </Button>
        </main>
      </ResponsiveWrapper>
    );
  }

  return (
    <ResponsiveWrapper customClass="!px-6 py-5 flex flex-1 flex-col">
      <Loader loading={isLoading || isSaving} />

      <main className="flex flex-1 flex-col">
        <div className="relative mb-2">
          <BackToModelsButton listPath={aidStudioListPath} />

          {isNewModel ? (
            <div className="flex items-center gap-5">
              <Heading variant="h1" customClass="shrink-0 text-xl !font-bold">
                New model
              </Heading>

              <div className="flex gap-3 items-center ml-auto shrink-0">
                <Button
                  type="button"
                  variant="reserve-text"
                  size="sm"
                  onClick={handleCancel}
                  data-testid="cancel-new-model-button"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="brand-filled"
                  size="sm"
                  onClick={handleSaveModel}
                  disabled={!isValid || isSaving}
                  loading={isSaving}
                  data-testid="save-model-button"
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <ModelHeader
              name={modelData?.name || 'Aid model'}
              canEdit={!isReadOnly}
              canArchive={!!modelData?.canArchive}
              isArchived={!!modelData?.archived}
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={isSaving}
              datasetCreatedDate={datasetData?.datasetTableCreatedDate}
              lastRecreatedDate={datasetData?.lastRecreatedDate}
              onRename={handleRename}
              onSave={handleSaveModel}
              onRevertChanges={handleRevertChanges}
              onSaveAsNewVersion={modelCopy.openSaveAsNewVersion}
              onCopyToEdit={modelCopy.openCopyToEdit}
              onArchive={() => modelId && archive.requestArchive(modelId)}
              onRestore={() => modelId && void archive.restore(modelId)}
              onQuickSummary={quickSummary.open}
              showQuickSummary={!isLastStep}
            />
          )}

          {quickSummary.isOpen && (
            <QuickSummaryPanel
              data={quickSummary.data}
              isLoading={quickSummary.isLoading}
              isError={quickSummary.isError}
              errorMessage={quickSummary.errorMessage}
              panelRef={quickSummary.panelRef}
              onClose={quickSummary.close}
              onFullResults={handleFullResults}
            />
          )}
        </div>

        {!isNewModel && (
          <div className="flex items-center justify-between mb-2">
            <AidStudioStepper
              steps={visibleSteps}
              activeStep={activeStep}
              onStepChange={handleStepChange}
              readOnly={isReadOnly}
            />
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="h-full">
          {renderStepContent()}
        </form>
      </main>

      {archive.isArchiveModalOpen && (
        <ArchiveModelModal
          loading={archive.isUpdating}
          onConfirm={archive.confirmArchive}
          onClose={archive.cancelArchive}
        />
      )}

      {modelCopy.mode && (
        <CopyModelModal
          headerText={getCopyModalHeader(modelCopy.mode)}
          onConfirm={modelCopy.handleCopy}
          onClose={modelCopy.closeModal}
        />
      )}
    </ResponsiveWrapper>
  );
};

export default AidStudioDetails;
